import React, { useState } from 'react';
import { Box, Dialog, IconButton } from '@mui/material';
import { X } from 'lucide-react';
import { useResponsive } from '../../../utils/responsive';
import { useTranslation } from 'react-i18next';
import RejectedIcon from '../../../../assets/rejected.svg';
import { useRejectOfferRequestMutation } from '../../../../api/senderApi3';
import { useRejectOfferRequestMutation as useRejectOfferAstravellerMutation } from '../../../../api/travellerApi3';
import { useToastStore } from '../../../../app/store/useToastStore';
import { useMarkNotificationAsRead } from '../../../../api/NotificationApi';

interface DeclineOfferDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (reason: string) => void;
  onSuccess?: () => void;
  isLoading?: boolean;
  userType?: 'sender' | 'traveler';
  orderCode?: string;
  notificationId?: string | number;
}

const MAX_CHARACTERS = 240;

export const DeclineOfferDialog: React.FC<DeclineOfferDialogProps> = ({
  open,
  onClose,
  onSubmit,
  onSuccess,
  isLoading = false,
  userType = 'traveler',
  orderCode,
  notificationId,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const senderRejectMutation = useRejectOfferRequestMutation();
  const travellerRejectMutation = useRejectOfferAstravellerMutation();
  const markNotificationAsReadMutation = useMarkNotificationAsRead();
  const { showToast } = useToastStore();

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= MAX_CHARACTERS) {
      setReason(value);
      setError('');
    }
  };

  // If orderCode is provided, use internal mutations, otherwise use external isLoading
  const loading = orderCode
    ? (senderRejectMutation.isPending || travellerRejectMutation.isPending)
    : isLoading;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(t('declineOffer.reasonRequired'));
      return;
    }

    // If no orderCode is provided, use the onSubmit callback (for external handling)
    if (!orderCode && onSubmit) {
      onSubmit(reason.trim());
      if (notificationId) {
        markNotificationAsReadMutation.mutate(notificationId);
      }
      return;
    }

    // If orderCode is provided, handle API call internally
    if (orderCode) {
      try {
        const params = { order_code: orderCode, reason: reason.trim() };

        if (userType === 'sender') {
          await senderRejectMutation.mutateAsync(params);
        } else {
          await travellerRejectMutation.mutateAsync(params);
        }
        if (notificationId) {
          markNotificationAsReadMutation.mutate(notificationId);
        }

        showToast('Offer has been declined successfully', 'success');
        setReason('');
        setError('');
        onClose();
        onSuccess?.();
      } catch {
        showToast('Failed to decline offer. Please try again.', 'error');
      }
    }
  };

  const handleClose = () => {
    // Prevent closing while loading
    if (loading) return;
    setReason('');
    setError('');
    onClose();
  };

  const recipientLabel = userType === 'sender' ? 'traveler' : 'sender';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      disableEscapeKeyDown={loading}
      sx={{
        zIndex: 9999,
      }}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 0,
          isolation: 'isolate',
          width: { xs: '90%', sm: '400px' },
          maxWidth: '400px',
          background: '#FFFFFF',
          boxShadow:
            '0px 20px 24px -4px rgba(10, 13, 18, 0.08), 0px 8px 8px -4px rgba(10, 13, 18, 0.03), 0px 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
          borderRadius: '12px',
          position: 'relative',
          overflow: 'hidden',
          margin: { xs: '16px', sm: '32px' },
          zIndex: 9999,
        },
      }}
    >
      {/* Background pattern decorative */}
      <Box
        sx={{
          position: 'absolute',
          width: '336px',
          height: '336px',
          left: '-120px',
          top: '-120px',
          zIndex: 0,
          pointerEvents: 'none',
        }}
      >
        {/* Concentric circles */}
        {[96, 144, 192, 240, 288, 336].map((size) => (
          <Box
            key={size}
            sx={{
              boxSizing: 'border-box',
              position: 'absolute',
              width: `${size}px`,
              height: `${size}px`,
              left: `calc(50% - ${size}px/2)`,
              top: `calc(50% - ${size}px/2)`,
              border: '1px solid #E9EAEB',
              borderRadius: '50%',
            }}
          />
        ))}
      </Box>

      {/* Modal Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 0,
          isolation: 'isolate',
          width: '100%',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: { xs: '20px 16px 0px', sm: '24px 24px 0px' },
            gap: '20px',
            width: '100%',
          }}
        >
          {/* Featured icon */}
          <Box
            sx={{
              width: '48px',
              height: '48px',
              background: '#F2E6EE',
              borderRadius: '9999px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Box
              component="img"
              src={RejectedIcon}
              alt="Decline"
              sx={{
                width: '24px',
                height: '24px',
              }}
            />
          </Box>

          {/* Close button */}
          <IconButton
            onClick={handleClose}
            disabled={loading}
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '8px',
              position: 'absolute',
              width: '44px',
              height: '44px',
              right: '16px',
              top: '16px',
              borderRadius: '8px',
              zIndex: 10,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <X size={24} color="#A4A7AE" />
          </IconButton>

          {/* Text and supporting text */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 0,
              gap: '8px',
              width: '100%',
            }}
          >
            {/* Title */}
            <Box
              component="h2"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: { xs: '16px', sm: '18px' },
                lineHeight: '27px',
                color: '#181D27',
                margin: 0,
              }}
            >
              Decline Offer
            </Box>

            {/* Supporting text */}
            <Box
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: { xs: '13px', sm: '14px' },
                lineHeight: '21px',
                color: '#535862',
              }}
            >
              Let {recipientLabel} know why you're declining this offer.
            </Box>
          </Box>

          {/* Textarea input field */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: 0,
              gap: '6px',
              width: '100%',
            }}
          >
            {/* Label */}
            <Box
              component="label"
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                color: '#484A54',
              }}
            >
              {t('declineOffer.reasonLabel')}
            </Box>

            {/* Input */}
            <Box
              component="textarea"
              value={reason}
              onChange={handleReasonChange}
              placeholder={t('declineOffer.reasonPlaceholder')}
              disabled={loading}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-start',
                padding: '12px 14px',
                gap: '8px',
                width: '100%',
                minHeight: '131px',
                background: '#FFFFFF',
                border: error ? '1px solid #DC2626' : '1px solid #D0D5DD',
                boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
                borderRadius: '8px',
                fontFamily: "'Poppins', sans-serif",
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '18px',
                color: '#667085',
                resize: 'vertical',
                outline: 'none',
                '&:focus': {
                  border: error ? '1px solid #DC2626' : '1px solid #9B1B5A',
                },
                '&::placeholder': {
                  color: '#667085',
                },
                '&:disabled': {
                  backgroundColor: '#F9FAFB',
                  cursor: 'not-allowed',
                },
              }}
            />

            {/* Hint text / Error */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: error ? '#DC2626' : '#475467',
                }}
              >
                {error || `Character limit: ${MAX_CHARACTERS} maximum`}
              </Box>
              <Box
                sx={{
                  fontFamily: "'Inter', sans-serif",
                  fontStyle: 'normal',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: reason.length >= MAX_CHARACTERS ? '#DC2626' : '#667085',
                }}
              >
                {reason.length}/{MAX_CHARACTERS}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Modal actions */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: '32px 0px 0px',
            width: '100%',
          }}
        >
          {/* Content */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'flex-start',
              padding: { xs: '0px 16px 10px', sm: '0px 24px 14px' },
              gap: '12px',
              width: '100%',
            }}
          >
            {/* Close Button */}
            <Box
              component="button"
              onClick={handleClose}
              disabled={loading}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '10px 20px',
                gap: '8px',
                flex: 1,
                height: '41px',
                background: '#FFFFFF',
                border: '1px solid #D9DADF',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontFamily: "'Poppins', sans-serif",
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                color: '#484A54',
                transition: 'all 0.2s',
                '&:hover': {
                  background: loading ? '#FFFFFF' : '#F9FAFB',
                },
              }}
            >
              Close
            </Box>

            {/* Submit Button */}
            <Box
              component="button"
              onClick={handleSubmit}
              disabled={loading || !reason.trim()}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '10px 20px',
                gap: '8px',
                flex: 1,
                height: '41px',
                background:
                  loading || !reason.trim() ? '#B8B8B8' : '#7A0056',
                borderRadius: '8px',
                border: 'none',
                cursor:
                  loading || !reason.trim() ? 'not-allowed' : 'pointer',
                fontFamily: "'Poppins', sans-serif",
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '21px',
                color: '#FFFFFF',
                transition: 'all 0.2s',
                '&:hover': {
                  background:
                    loading || !reason.trim() ? '#B8B8B8' : '#5D0042',
                },
              }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};
